import { HandThumbDownIcon, HandThumbUpIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { format } from "date-fns";
import React, { useEffect, useState } from "react";

import { Dialog, Input, Paragraph } from "@fronterahealth/frontera-ui-components";

import { useCreateMediaTargetPredictionFeedbackMutation } from "@api/graphql/types-and-hooks";
import { notifyError } from "@components/notifications/notifications";
import { GoalMomentMediaTargetPrediction } from "@providers/VideoSessionProviderV2";

interface MediaTargetPredictionFeedbackDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  mediaTargetPrediction: GoalMomentMediaTargetPrediction | undefined;
}

export const MediaTargetPredictionFeedbackDialog: React.FC<MediaTargetPredictionFeedbackDialogProps> = ({
  open,
  setOpen,
  mediaTargetPrediction,
}) => {
  const [suggestion, setSuggestion] = useState<string>("");
  const [reasonOfRating, setReasonOfRating] = useState<string>("");
  const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);

  // isAccurate reflects the same naming as the data model
  const [isAccurate, setIsAccurate] = useState<boolean>();

  const latestFeedback = mediaTargetPrediction?.mediaTargetFeedbacks?.edges.sort(
    (a, b) => new Date(b?.node?.lastUpdatedDate).getTime() - new Date(a?.node?.lastUpdatedDate).getTime(),
  )[0];

  const latestFeedbackDate = latestFeedback?.node?.lastUpdatedDate
    ? format(new Date(latestFeedback.node.lastUpdatedDate), "MM/dd/yyyy")
    : "";

  const handleFeedbackChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsAccurate(e.target.value === "good");
  };

  const createMediaTargetPredictionFeedback = useCreateMediaTargetPredictionFeedbackMutation({});
  const { isPending } = createMediaTargetPredictionFeedback;

  const submitFeedback = async () => {
    if (isAccurate === undefined) {
      notifyError("Please select how we did.");
      return;
    }

    try {
      const response = await createMediaTargetPredictionFeedback.mutateAsync({
        input: {
          mediaTargetPredictionId: mediaTargetPrediction?.id || "",
          isAccurate: isAccurate,
          reasonOfRating: reasonOfRating || "",
          improvementComment: suggestion || "",
        },
      });

      if (!response?.createMediaTargetPredictionFeedback?.status) {
        notifyError(response?.createMediaTargetPredictionFeedback?.message || "Failed to submit feedback.");
        return;
      }

      setIsFormSubmitted(true);
    } catch (err) {
      console.error("Submission error", err);
      notifyError("Failed to submit feedback.");
    }
  };

  useEffect(() => {
    setSuggestion("");
    setReasonOfRating("");
    setIsAccurate(undefined);
    if (open) {
      setIsFormSubmitted(false);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      setOpen={setOpen}
      title="Help Us Improve"
      primaryButton={
        !isFormSubmitted
          ? {
              text: isPending ? "Submitting..." : "Submit",
              onClick: submitFeedback,
              disabled: isAccurate === undefined || !reasonOfRating.length,
            }
          : undefined
      }
      secondaryButton={!isFormSubmitted ? { text: "Cancel", onClick: () => setOpen(false) } : undefined}
      size="xl"
    >
      {isFormSubmitted ? (
        <div>
          <Paragraph displayType="normal" colorType="secondary" className="pb-6">
            Thank you for your feedback!
          </Paragraph>
        </div>
      ) : (
        <div className="flex flex-col">
          <Paragraph displayType="normal" colorType="primary" className="pb-2">
            How did we do?
          </Paragraph>
          <div className="relative flex justify-between gap-3 items-center min-w-fit flex-1 w-full">
            <label className="flex flex-1 justify-between cursor-pointer items-center rounded-full border border-limestone-100 px-4 py-2 text-text-primary transition-all hover:border-limestone-200 has-checked:bg-limestone-50">
              <input
                name="feedback"
                aria-describedby="comments-description"
                value="good"
                type="radio"
                className="peer sr-only"
                onChange={handleFeedbackChange}
              />
              <HandThumbUpIcon className="w-5 h-5 text-limestone-500" />
              Good
              <CheckCircleIcon className="h-6 w-6 rounded-full bg-limestone-50 text-limestone-50 transition-all peer-checked:rounded-none peer-checked:bg-transparent peer-checked:text-green-700 ml-4" />
            </label>

            <label className="flex flex-1 justify-between cursor-pointer items-center rounded-full border border-limestone-100 px-4 py-2 text-text-primary transition-all hover:border-limestone-200 has-checked:bg-limestone-50">
              <HandThumbDownIcon className="w-5 h-5 text-limestone-500" />
              <input
                name="feedback"
                aria-describedby="comments-description"
                value="bad"
                type="radio"
                className="peer sr-only"
                onChange={handleFeedbackChange}
              />
              Bad
              <CheckCircleIcon className="h-6 w-6 rounded-full bg-limestone-50 text-limestone-50 transition-all peer-checked:rounded-none peer-checked:bg-transparent peer-checked:text-green-700 ml-4" />
            </label>
          </div>

          {isAccurate != undefined && (
            <div className="pt-4">
              <Input
                label="Why did you choose this rating?"
                name="suggestion"
                placeholder="Add your reason..."
                required={false}
                inputSize="full"
                value={reasonOfRating}
                onChange={(e) => setReasonOfRating(e.target.value)}
                maxLength={300}
                hintText={`${300 - reasonOfRating.length} remaining`}
              />

              <Input
                label="How else can we improve?"
                name="suggestion"
                placeholder="Share your suggestion..."
                required={false}
                inputSize="full"
                hintText="Optional"
                value={suggestion}
                onChange={(e) => setSuggestion(e.target.value)}
              />
            </div>
          )}
          {latestFeedbackDate && (
            <div className="flex flex-col p-4 mt-4 rounded-lg bg-orange-50">
              <Paragraph colorType="primary" displayType="loud" className="font-bold">
                Feedback received!
              </Paragraph>
              <Paragraph colorType="primary">
                We've logged your feedback on this moment but you can share more anytime.
              </Paragraph>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
};
